import React, { MouseEvent, MouseEventHandler, useState } from "react";
import styled from "@emotion/styled";
import { App } from "antd5";
import { capitalize } from "lodash";
import { Flex } from "styles/utility-components";

import HoverAction from "components/ui/hover_action/HoverAction";
import { useRemoveOpportunityEntity } from "lib/hooks/api/opportunities/useRemoveOpportunityEntity";
import { useSubscription } from "lib/hooks/subscription";
import UIcon from "lib/icons/untitled_ui/UIcon";
import { usePaywall } from "lib/providers/Paywall";
import { routes } from "lib/routes";
import { EventNames, useTracking } from "lib/tracking";
import { DataTypeNameWithBuyersAndSuppliers } from "lib/types/models";
import { OpportunityEntityType } from "../../../lib/types/graphQLEnums";
import { convertEntityTypeToOpportunityEntityType } from "../saving/types";

export type Entities = "buyer" | "supplier";

type Props = {
  opportunityId: string;
  entityType: Entities;
  url: string;
  entities?: { id: string; name: string }[];
};

const entityToDataType = (entity: Entities): DataTypeNameWithBuyersAndSuppliers => {
  switch (entity) {
    case "buyer":
      return "BUYERS";
    case "supplier":
      return "SUPPLIERS";
  }
};

const getIconByEntityType = (entityType: Entities) => {
  switch (entityType) {
    case "buyer":
      return "wallet02";
    case "supplier":
      return "building07";
  }
};

export default function EntitySelector({ opportunityId, entityType, entities, url }: Props) {
  const { logEvent } = useTracking();
  const { openPaywallModal } = usePaywall();
  const subscription = useSubscription();
  const { message } = App.useApp();

  const [isExpanded, setIsExpanded] = useState(false);

  const dataType = entityToDataType(entityType);
  const hasDataType = subscription.hasDataTypes(dataType);
  const paywall = dataType === "SUPPLIERS" ? "SUPPLIERS" : null;
  const hasPaywall = paywall && !hasDataType;

  const onClick = (evt: MouseEvent<HTMLAnchorElement>) => {
    if (hasPaywall) {
      evt.preventDefault();
      openPaywallModal(paywall);
    } else {
      logEvent(EventNames.addOpportunityDataClicked, {
        "Data type": entityType.toUpperCase(),
      });
    }
  };

  const { mutate: removeEntity } = useRemoveOpportunityEntity({
    onSuccess: () => {
      message.open({
        icon: null,
        content: (
          <Flex gap={8} alignItems="center">
            <UIcon icon="checkCircle" />
            <span>{`${capitalize(entityType)} removed`}</span>
          </Flex>
        ),
      });
    },
    onError: () => message.error(`Failed to remove ${entityType}`),
  });

  const handleRemoveEntity = (e: MouseEvent<HTMLButtonElement>, id: string) => {
    e.preventDefault();
    e.stopPropagation();
    removeEntity({
      id: opportunityId,
      entities: {
        entities: [
          {
            entityId: id,
            entityType: convertEntityTypeToOpportunityEntityType(entityType, OpportunityEntityType),
          },
        ],
      },
    });
  };

  const handleAddEntity: MouseEventHandler = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (hasPaywall) {
      openPaywallModal(paywall);
    } else {
      window.location.href = url;
    }
  };

  const toggleExpand: MouseEventHandler = (e) => {
    e.preventDefault();
    setIsExpanded(!isExpanded);
  };

  if (!entities?.length) {
    return (
      <Flex alignItems="center" height={32}>
        <StyledLink href={hasPaywall ? undefined : url} onClick={onClick}>
          Click to add
        </StyledLink>
      </Flex>
    );
  }

  const visibleEntities = isExpanded ? entities : entities.slice(0, 2);
  const remainingCount = entities.length - 2;
  return (
    <Flex column gap={4}>
      {visibleEntities.map(({ id, name }) => (
        <HoverAction
          key={id}
          link={hasPaywall ? undefined : `${routes.buyer}/${id}`}
          label={name}
          icon={getIconByEntityType(entityType)}
          secondaryIcon="minus"
          primaryIcon="plus"
          onSecondaryClick={(e) => handleRemoveEntity(e, id)}
          onPrimaryClick={handleAddEntity}
        />
      ))}
      {entities.length > 2 && (
        <StyledLink onClick={toggleExpand}>
          {isExpanded ? "Show less" : `${remainingCount} more`}
        </StyledLink>
      )}
    </Flex>
  );
}

const StyledLink = styled.a(({ theme }) => ({
  all: "unset",
  cursor: "pointer",
  display: "block",
  width: "100%",
  padding: "0 11px",
  color: theme.colors.sysPrimaryDefault,
  "&:hover": {
    color: theme.colors.sysPrimaryHover,
  },
}));
